import React, { useCallback, useEffect, useState } from "react";
import { Col, Container } from "reactstrap";
import { assignBulkTaskApi, retrieveAllTasks, startTask } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import { Spin } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import resumeIcon from "assets/images/svg/restartIcon.svg";
import startIcon from "assets/images/svg/playIcon.svg";
import scheduleIcon from "assets/images/svg/scheduleIcon.svg";
import { AUTH_USER } from "utils/constants";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import SetAvailabilityModal from "pages/customer-management/customerDetails/SetAvaliabilityModal";
import NGIEmpty from "components/Common/components/NGIEmpty";

const useStyles = makeStyles({
  itemContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    padding: 15,
    borderRadius: 5,
    border: "1px solid rgba(0, 0, 0, 0.10)",
    background: "#FFF",
    marginBottom: 10,
  },
  item: {
    fontWeight: 600,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    paddingRight: 5,

    "& span": {
      color: "#8C8C8C",
      fontSize: 14,
      fontWeight: 300,
      marginRight: 5,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "5px",
  },
  button: {
    padding: "6px 12px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "3px",
    borderRadius: 2,
    fontWeight: 600,
    fontSize: 9,

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  resume: {
    backgroundColor: "#0085FF26",
    color: "#0085FF",
  },
  start: {
    backgroundColor: "#D8FFE3",
    color: "#17C347",
  },
  schedule: {
    backgroundColor: "#DB660726",
    color: "#DB6607",
  },
});

const ALLOWED_STATUSES = ["In progress", "Assigned"];
const InterventionCall = () => {
  const classes = useStyles();
  const [isLoading, setLoader] = useState(false);
  const [tasksList, setTasksList] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [isSetAvaliabilityOpen, setIsSetAvaliabilityOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  useEffect(() => {
    handleSearch();
  }, []);

  const openAssignModal = useCallback(() => setAssignOpen(true), []);
  const closeAssignModal = useCallback(() => setAssignOpen(false), []);

  const openStartModal = useCallback(() => setStartOpen(true), []);
  const closeStartModal = useCallback(() => setStartOpen(false), []);

  const onSetAvailability = useCallback(() => setIsSetAvaliabilityOpen(true), []);
  const onSetAvailabilityClose = useCallback(() => setIsSetAvaliabilityOpen(false), []);

  const handleSearch = () => {
    setLoader(true);

    retrieveAllTasks({
      category: "Intervention Call",
      depot: AUTH_USER()?.depotName,
      pageSize: 1000,
    })
      .then(res => {
        const {
          result: { items },
        } = res?.data;
        if (items?.length === 0) {
          warnMessage("Tasks were not found");
        }
        const sorted = res?.data?.result?.items
          ?.filter(item => ALLOWED_STATUSES.includes(item.status))
          ?.sort((a, b) => a.customer.name.localeCompare(b.customer.name));

        setTasksList(sorted || []);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        setTasksList(null);
        defaultNgiErrorMethod(err);
      });
  };

  const onAssign = () => {
    setLoader(true);
    const { email, depotName } = AUTH_USER();

    const options = {
      assignTo: email,
      assignToDepot: depotName,
      taskIds: [activeItem.id],
    };
    closeAssignModal();
    assignBulkTaskApi(options)
      .then(() => {
        onStartTask();
      })
      .catch(error => {
        setLoader(false);
        defaultNgiErrorMethod(error);
      });
  };

  const onStartTask = () => {
    setLoader(true);
    startTask(activeItem.id)
      .then(() => {
        successMessage("Task was started successfully");
        handleSearch();
        setLoader(false);
      })
      .catch(e => {
        setLoader(false);
        defaultNgiErrorMethod(e);
      });
  };

  const handleStart = useCallback(
    event => {
      const id = event.currentTarget.dataset.id;
      const foundItem = tasksList.find(item => item.id === id);
      setActiveItem(foundItem);
      const { assignedTo } = foundItem;
      if (!assignedTo || assignedTo !== AUTH_USER()?.email) {
        openAssignModal();
        return;
      }
      openStartModal();
    },
    [tasksList]
  );

  const handleSchedule = useCallback(
    event => {
      const id = event.currentTarget.dataset.id;
      const foundItem = tasksList.find(item => item.id === id);
      setActiveItem(foundItem);
      onSetAvailability();
    },
    [tasksList]
  );

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Spin spinning={isLoading}>
            <Col lg={12}>
              {tasksList.length ? (
                tasksList.map(item => (
                  <div className={classes.itemContainer} key={item.id}>
                    <div className={classes.item} title={item.customer.name}>
                      {item.customer.name}
                    </div>
                    <div className={classes.item} title={item.customer.id}>
                      <span>Account ID:</span>
                      {item.customer.id}
                    </div>
                    <div className={classes.item} title={item.customer.phone}>
                      <span>Phone:</span>
                      {item.customer.phone}
                    </div>
                    <div className={classes.item} title={item.depot}>
                      <span>Depot:</span>
                      {item.depot}
                    </div>
                    <div className={classes.actions}>
                      {item.status === "In progress" && (
                        <div data-id={item.id} className={classnames(classes.button, classes.resume)}>
                          <img src={resumeIcon} alt="resumeIcon" />
                          RESUME TASK
                        </div>
                      )}
                      {item.status === "Assigned" && (
                        <>
                          <div
                            onClick={handleSchedule}
                            data-id={item.id}
                            className={classnames(classes.button, classes.schedule)}
                          >
                            <img src={scheduleIcon} alt="scheduleIcon" />
                            SCHEDULE
                          </div>
                          <div
                            onClick={handleStart}
                            data-id={item.id}
                            className={classnames(classes.button, classes.start)}
                          >
                            <img src={startIcon} alt="startIcon" />
                            START TASK
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <NGIEmpty />
              )}
            </Col>
          </Spin>
          {assignOpen && (
            <ConfirmationModal
              title="Assign to continue"
              description="To start working on this task, you need to assign it to yourself. Do you want to assign it now?"
              onCancel={closeAssignModal}
              onConfirm={onAssign}
            />
          )}
          {startOpen && (
            <ConfirmationModal
              description="Are you sure you want to start task??"
              onCancel={closeStartModal}
              onConfirm={onStartTask}
            />
          )}
          {isSetAvaliabilityOpen && (
            <SetAvailabilityModal
              update={handleSearch}
              customerId={activeItem?.customer?.id}
              onClose={onSetAvailabilityClose}
            />
          )}
        </Container>
      </div>
    </>
  );
};

export default InterventionCall;
